import { useEffect, useState } from 'react';
import { useT } from '@hana/i18n';
import { Button, Drawer, Input, Modal, useToast } from '@hana/ui';
import { IconDeviceFloppy, IconPlus, IconX } from '@tabler/icons-react';
import './SetupDrawer.i18n';
import s from './setupdrawer.module.css';

/** Ngăn kéo thêm / sửa / dán và hộp lịch sử của Thiết lập giá (board Mint-PriceSetup, `dw` · `md.kind=hist`). Ghi chờ bật DB. */
export type SetupForm = 'lcc' | 'hub' | 'tariff' | 'cus' | 'ctry';
export type SetupDrawerState = { kind: 'add' | 'edit'; form: SetupForm } | { kind: 'paste' };
type Field = readonly [label: string, value: string];
interface FormDef { title: string; lead: string; fields: (money: boolean) => Field[]; tpl: readonly string[]; req: string }

const FORMS: Record<SetupForm, (t: (k: string) => string) => FormDef> = {
  lcc: (t) => ({ title: t('sd.lccT'), lead: t('sd.lccL'), fields: (m) => [['LocalChargeCode', 'RF_MSC_Non-AMS'], [t('sd.fCarrier'), 'MSC'], [t('sd.fSide'), 'POL'], [t('sd.fCharge'), ''], [t('sd.fNameEn'), ''], [t('sd.fUnit'), 'Unit'], ['Term', 'Prepaid'], ...(m ? ([['20′', ''], ['40′', '']] as Field[]) : []), [t('sd.fValid'), '23/Oct/2026']], tpl: ['Full LCC', 'INCL THC', 'NON AMS'], req: t('sd.fCharge') }),
  hub: (t) => ({ title: t('sd.hubT'), lead: t('sd.hubL'), fields: () => [[t('sd.fCarrier'), 'MSC'], ['POL', 'VNSGN'], [t('sd.fPod'), ''], [t('sd.fHub'), 'SGSIN'], [t('sd.fTransit'), ''], [t('sd.fNote'), '']], tpl: [], req: t('sd.fPod') }),
  tariff: (t) => ({ title: t('sd.tarT'), lead: t('sd.tarL'), fields: () => [[t('sd.fCharge'), ''], [t('sd.fGroup'), 'Plus Services'], [t('sd.fNameVn'), ''], [t('sd.fNameEn2'), ''], [t('sd.fUnit'), 'Shpt'], [t('sd.fCur'), 'VND'], ['20′', ''], ['40′', '']], tpl: [], req: t('sd.fCharge') }),
  cus: (t) => ({ title: t('sd.cusT'), lead: t('sd.cusL'), fields: () => [[t('sd.fCustomer'), 'THACO'], [t('sd.fCharge'), ''], [t('sd.fKind'), t('sd.kindOwn')], [t('sd.fCur'), 'USD'], ['20′', ''], ['40′', '']], tpl: [], req: t('sd.fCharge') }),
  ctry: (t) => ({ title: t('sd.ctryT'), lead: t('sd.ctryL'), fields: () => [[t('sd.fLocode'), ''], [t('sd.fPort'), ''], [t('sd.fCountry'), ''], [t('sd.fRegion'), ''], [t('sd.fHubDef'), ''], [t('sd.fNote'), '']], tpl: [], req: t('sd.fLocode') }),
};
const PASTE_SAMPLE = 'THC\tTerminal Handling Charge\tUnit\t180\t270\nSLF\tSeal Charge\tUnit\t10\t10\nBLF\tBill of Lading\tShpt\t45\t45';

export function SetupDrawer({ state, money, onClose }: { state: SetupDrawerState | null; money: boolean; onClose: () => void }) {
  const t = useT();
  const toast = useToast();
  const [vals, setVals] = useState<Record<string, string>>({});
  const [tpl, setTpl] = useState('');
  const [text, setText] = useState(PASTE_SAMPLE);
  const [err, setErr] = useState(false);
  const key = state ? (state.kind === 'paste' ? 'paste' : `${state.kind}|${state.form}`) : '';
  useEffect(() => { setVals({}); setTpl(''); setErr(false); setText(PASTE_SAMPLE); }, [key]);
  if (!state) return null;

  if (state.kind === 'paste') {
    const rows = text.split('\n').filter((l) => l.trim()).length;
    return (
      <Drawer open onOpenChange={(o) => !o && onClose()} title={t('sd.pasteT')} footer={
        <div className={s.foot}>
          <Button icon={<IconX size={16} aria-hidden="true" />} onClick={onClose}>{t('sd.cancel')}</Button>
          <span className={s.grow} />
          <Button variant="primary" onClick={() => { toast({ msg: t('sd.pasteOk', { n: rows }) }); toast({ msg: t('sd.noWrite'), tone: 'err' }); }}>{t('sd.pastePreview')}</Button>
        </div>
      }>
        <div className={s.body}>
          <div className={s.lead}>{t('sd.pasteL')}</div>
          <label className={s.field}>
            <span className={s.label}>{t('sd.pasteK')}</span>
            <textarea className={s.paste} aria-label={t('sd.pasteK')} value={text} onChange={(e) => setText(e.target.value)} />
          </label>
        </div>
      </Drawer>
    );
  }

  const def = FORMS[state.form](t);
  const edit = state.kind === 'edit';
  const fields = def.fields(money);
  const val = (k: string, d: string) => vals[k] ?? d;
  const save = () => {
    const miss = fields.some(([k, d]) => k === def.req && !val(k, d).trim());
    setErr(miss);
    if (!miss) toast({ msg: t('sd.noWrite'), tone: 'err' });
  };
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()} title={edit ? t('sd.editT', { v: def.title.replace(/^Thêm |^Add /, '') }) : def.title} footer={
      <div className={s.foot}>
        <Button icon={<IconX size={16} aria-hidden="true" />} onClick={onClose}>{t('sd.cancel')}</Button>
        <span className={s.grow} />
        <Button variant="primary" icon={edit ? <IconDeviceFloppy size={16} aria-hidden="true" /> : <IconPlus size={16} aria-hidden="true" />} onClick={save}>{edit ? t('sd.saveNew') : t('sd.add')}</Button>
      </div>
    }>
      <div className={s.body}>
        <div className={s.lead}>{def.lead}</div>
        {def.tpl.length > 0 && (
          <div className={s.tpls}>
            <span className={s.label}>{t('sd.quick')}</span>
            {def.tpl.map((k) => <button key={k} type="button" className={s.tplBtn} aria-pressed={tpl === k} onClick={() => setTpl(k)}>{k}</button>)}
          </div>
        )}
        <div className={s.fields}>
          {fields.map(([k, d]) => (
            <Input key={k} label={k} value={val(k, d)} error={err && k === def.req ? t('sd.need', { v: def.req }) : undefined} onChange={(e) => setVals((o) => ({ ...o, [k]: e.target.value }))} />
          ))}
        </div>
      </div>
    </Drawer>
  );
}

/** Hộp "Lịch sử thay đổi" (board `md.kind=hist`). */
export function SetupHistory({ open, code, onClose }: { open: boolean; code?: string; onClose: () => void }) {
  const t = useT();
  const items: [string, string][] = [['22/Oct 10:12', t('sd.h1')], ['15/Oct 09:30', t('sd.h2')], ['01/Oct 08:00', t('sd.h3')], ['20/Sep 14:22', t('sd.h4')]];
  return (
    <Modal open={open} onOpenChange={(o) => !o && onClose()} title={t('sd.histT') + (code ? ` · ${code}` : '')} description={t('sd.histL')} footer={<div className={s.mdFoot}><Button onClick={onClose}>{t('sd.close')}</Button></div>}>
      {items.map(([at, k]) => <div key={at} className={s.hrow}><span className={s.hat}>{at}</span><span>{k}</span></div>)}
    </Modal>
  );
}
